<template>

  <div class="card-base card-shadow--medium widget small-widget queue-widget">
    <div class="widget-value ph-10">
      <div class="flex mb-10 mt-10 align-center">
        <span class="mr-5 flex justify-center align-center">
          <v-svg width="18" height="18" class="voxo-red" sprite="queue"/>
        </span>
        <el-input
          class="widget-name-input ml-10"
          v-model="widgetName"
        />
        <el-button
          @click="removeWidget"
          class="table-action-button ml-10"
        >
          <i class="el-icon-delete voxo-dark-red" />
        </el-button>
      </div>
    </div>

    <div class="ph-10 flex">
      <el-input
        class="widget-search-input"
        v-model="searchText"
        placeholder="Search queue"
        clearable
        style="margin-right: 0;"
      >
        <template slot="prefix">
          Queue
        </template>
      </el-input>
      <div>
        <el-popover trigger="click" placement="right-start" popper-class="column-filter-popper">
          <template slot="reference">
            <el-button
              style="height: 50px; border-bottom-left-radius: 0; border-top-left-radius: 0;"
            >
              <i class="mdi mdi-filter voxo-blue" />
            </el-button>
          </template>
          <el-checkbox-group v-model="activeTableFields">
            <el-checkbox
              v-for="(column, index) in tableColumns"
              :key="index"
              :label="column.field"
            >
              {{ column.title }}
            </el-checkbox>
          </el-checkbox-group>
        </el-popover>
      </div>
    </div>

    <div class="table-widget-table ph-10">
      <vxe-grid
        round
        height="100%"
        border="inner"
        ref="xGrid"
        class="user-stats-table"
        empty-text="No Data"
        auto-resize
        :show-overflow="true"
        :scroll-y="{enabled: true, gt: 25, oSize: 0}"
        :sort-config="{defaultSort: {field: 'name', order: 'asc'}}"
        :data="tableData"
        :columns="activeTableColumns"
      />
    </div>

  </div>

</template>

<script>

import {mapGetters} from "vuex";
import {debounce} from 'lodash';

export default {
  name: 'QueuesTable',
  props: {
    widgetIndex: {required: true},
    title: {required: true},
    queueNameKey: {required: true},
    columnFields: {required: true},
  },
  data() {
    return {
      widgetName: this.title,
      searchText: '',
      filter: '',
      viewStyle: 'default',
      tableColumns: [
        {
          field: 'queueId',
          title: 'Queue',
          minWidth: 175,
          sortable: true,
          formatter: this.getQueueName,
        },
        {
          field: 'inboundComplete',
          title: 'Inbound Complete',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'avgTimeToAnswer',
          title: 'Avg Answer Time',
          minWidth: 175,
          sortable: true,
          slots: {
            default: ({row}) => {
              return [
                <span>{this.formatTime(row.avgTimeToAnswer)}</span>
              ]
            }
          }
        },
        {
          field: 'attendedTransfers',
          title: 'Attended Transfers',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'blindTransfers',
          title: 'Blind Transfers',
          minWidth: 175,
          sortable: true
        },
        {
          field: 'totalTransfers',
          title: 'Total Transfers',
          minWidth: 175,
          sortable: true
        },
        {
          field: 'exitKey',
          title: 'Exits Key',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'exitTimeout',
          title: 'Exits Timeout',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'totalExits',
          title: 'Exits Total',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'abandoned',
          title: 'Abandoned',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'abandonRate',
          title: 'Abandon Rate',
          minWidth: 175,
          sortable: true,
          slots: {
            default: ({row}) => {
              return [
                <span>{row.abandonRate > 0 ? `${row.abandonRate} %` : row.abandonRate}</span>
              ]
            }
          }
        },
        {
          field: 'avgAbandonWaitTime',
          title: 'Avg Abandon Wait',
          minWidth: 175,
          sortable: true,
          slots: {
            default: ({row}) => {
              return [
                <span>{this.formatTime(row.avgAbandonWaitTime)}</span>
              ]
            }
          }
        },
        {
          field: 'totalInbound',
          title: 'Inbound Total',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'refused',
          title: 'Refuse Events',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'outboundAnswered',
          title: 'Outbound Answered',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'outboundNoAnswer',
          title: 'Outbound No Answer',
          minWidth: 175,
          sortable: true,
        },
        {
          field: 'totalOutbound',
          title: 'Outbound Total',
          minWidth: 175,
          sortable: true,
        }
      ]
    }
  },
  computed: {

    ...mapGetters('queueDashQueues', {queueStoreData: 'list'}),

    tableData() {

      if(this.filter) {
        return this.queueStoreData.filter(item => {
          return this.queueNameKey[item.queueId].toLowerCase().includes(this.filter.toLowerCase());
        });
      }

      return this.queueStoreData;
    },

    activeTableColumns() {
      return this.tableColumns.filter(column => this.columnFields.includes(column.field));
    },

    activeTableFields: {
      get: function () {
        return this.tableColumns.map(column => column.field).filter(field => this.columnFields.includes(field));
      },
      set: debounce(function (newValue) {
        //emit an event to change the column fields for the layout index
        this.$emit('updateActiveColumns', {columns: newValue, index: this.widgetIndex});
      }, 150)
    }

  },
  watch: {
    searchText: debounce(function () {
      this.filter = this.searchText;
    }, 250),
    widgetName: debounce(function() {
      if(this.widgetName === this.title) return;
      this.$emit('nameUpdate', {name: this.widgetName, index: this.widgetIndex});
    }, 750)
  },
  methods: {

    getQueueName(data) {
      const { cellValue } = data;
      return this.queueNameKey[cellValue] ? this.queueNameKey[cellValue] : 'Unknown';
    },

    removeWidget(){
      this.$emit('removeWidget', this.widgetIndex);
    },

    //input is time in seconds. output is pretty time.
    formatTime(time) {
      const hours = Math.floor(time / 3600);
      const remainderSeconds = time % 3600;
      const minutes = Math.floor(remainderSeconds / 60);
      const seconds = Math.round(remainderSeconds % 60);

      if (hours > 0) {return hours + 'hr ' + minutes + 'm';}
      if (minutes > 0) {return minutes + 'm ' + seconds + 's';}
      if (seconds > 0) {return seconds + 's';}
      return '0';
    },

  }
}

</script>
